import React, { useState, useMemo, useEffect } from "react";
// ... import ทั้งหมดเหมือนเดิม
import {
  Box,
  Heading,
  Select,
  Button,
  VStack,
  HStack,
  Text,
  Flex,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Spinner,
  Center,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  Wrap,
} from "@chakra-ui/react";
import { useSearchParams, useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import * as XLSX from "xlsx";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { authFetch } from "../../utils/authFetch";
import HistoryChart from "../../components/history/HistoryChart";
import { ChevronLeftIcon} from "@chakra-ui/icons"; 

const formatDate = (d) => new Date(d).toISOString().split("T")[0];

export default function DeviceHistoryPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const deviceId = searchParams.get("device") ?? "-";

  // State for control panel
  const [dataType, setDataType] = useState("hourly");
  const [rangeType, setRangeType] = useState("last7days");
  const [selectedMonth, setSelectedMonth] = useState(
    formatDate(new Date()).slice(0, 7)
  );
  const [customStart, setCustomStart] = useState(formatDate(new Date()));
  const [customEnd, setCustomEnd] = useState(formatDate(new Date()));
  const [selectedMetric, setSelectedMetric] = useState("pm25");
  const [chartType, setChartType] = useState("line");

  // --- REQ 1: State สำหรับ Checkbox ---
  const [showAvg, setShowAvg] = useState(true);
  const [showMin, setShowMin] = useState(false);
  const [showMax, setShowMax] = useState(false);

  // State for data
  const [tableData, setTableData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showTable, setShowTable] = useState(true);

  const availableMetrics = [
    { key: "pm25", name: "PM2.5" },
    { key: "pm10", name: "PM10" },
    { key: "tsp", name: "TSP" },
    { key: "noise", name: "Noise" },
  ];

  const handleFetchData = async () => {
    setIsLoading(true);
    let start, end;
    const now = new Date();

    switch (rangeType) {
      case "last7days":
        end = new Date();
        start = new Date();
        start.setDate(now.getDate() - 7);
        break;
      case "last14days":
        end = new Date();
        start = new Date();
        start.setDate(now.getDate() - 14);
        break;
      case "last30days":
        end = new Date();
        start = new Date();
        start.setDate(now.getDate() - 30);
        break;
      case "month":
        start = new Date(selectedMonth + "-01");
        end = new Date(
          start.getFullYear(),
          start.getMonth() + 1,
          0,
          23,
          59,
          59
        );
        break;
      case "custom":
        start = new Date(customStart);
        end = new Date(customEnd);
        end.setHours(23, 59, 59, 999);
        break;
      default:
        start = new Date();
        end = new Date();
    }

    try {
      const token = localStorage.getItem("token");
      const res = await authFetch(
        `${
          process.env.REACT_APP_API_URL
        }/api/data/${dataType}?deviceId=${deviceId}&start=${start.toISOString()}&end=${end.toISOString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to fetch data");

      setTableData(data);
      toast({
        title: "Data loaded successfully!",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
    } catch (err) {
      toast({
        title: "Error loading data",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      setTableData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const exportToExcel = () => {
    // 1. สร้างข้อมูลชุดใหม่สำหรับการ Export โดย map ผ่าน tableData
    const dataForExport = tableData.map((row) => {
      // 2. ในแต่ละ row, สร้าง object ใหม่ที่มี key-value ตรงตาม Header และข้อมูลที่จัดรูปแบบแล้ว
      // ***สำคัญ: ต้องปรับแก้ส่วนนี้ให้ตรงกับ columns config ของคุณ***
      if (dataType === "hourly" || dataType === "daily") {
      return {
        "Device ID": deviceId,
        Timestamp: new Date(
          row.timestamp || row.dayStart || row.hourStart
        ).toLocaleString("th-TH", { timeZone: "Asia/Bangkok" }), // ตัวอย่างการจัดรูปแบบวันที่
        "PM 2.5 (avg)": row.data?.avgPm25?.toFixed(1) || "-",
        "PM 2.5 (min)": row.data?.minPm25?.toFixed(1) || "-",
        "PM 2.5 (max)": row.data?.maxPm25?.toFixed(1) || "-",
        "PM 10 (avg)": row.data?.avgPm10?.toFixed(1) || "-",
        "PM 10 (min)": row.data?.minPm10?.toFixed(1) || "-",
        "PM 10 (max)": row.data?.maxPm10?.toFixed(1) || "-",
        "TSP (avg)" : row.data?.avgTsp?.toFixed(1) || "-",
        "TSP (min)": row.data?.minTsp?.toFixed(1) || "-",
        "TSP (max)": row.data?.maxTsp?.toFixed(1) || "-",
        "Noise (avg)": row.data?.avgNoise?.toFixed(1) || "-",
        "Noise (min)": row.data?.minNoise?.toFixed(1) || "-",
        "Noise (max)": row.data?.maxNoise?.toFixed(1) || "-",
      };
      }
      else if (dataType === "raw") {
        return {
          "Device ID": deviceId,
          Timestamp: new Date(
            row.timestamp || row.dayStart || row.hourStart
          ).toLocaleString("th-TH", { timeZone: "Asia/Bangkok" }),
          "PM 2.5":
            row.data?.pm25?.toFixed(1) || row.data?.avgPm25?.toFixed(1) || "-",
          "PM 10":
            row.data?.pm10?.toFixed(1) || row.data?.avgPm10?.toFixed(1) || "-",
          TSP: row.data?.tsp?.toFixed(1) || row.data?.avgTsp?.toFixed(1) || "-",
          Noise:
            row.data?.noise?.toFixed(1) ||
            row.data?.avgNoise?.toFixed(1) ||
            "-",
        };
      }
    });

    // 3. สร้าง worksheet จากข้อมูลที่จัดรูปแบบแล้ว
    const ws = XLSX.utils.json_to_sheet(dataForExport);

    // 4. สร้าง workbook และ export เป็นไฟล์ .xlsx
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "History");
    XLSX.writeFile(wb, `${deviceId}_${dataType}.xlsx`);

    // (Optional) แจ้งเตือนผู้ใช้
    toast({
      title: "Export Success",
      description: "Data has been exported to Excel successfully.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  const exportChartToPDF = () => {
    const chartElement = document.getElementById("chart-area");
    if (!chartElement) return;
    html2canvas(chartElement).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("landscape", "pt", "a4");
      const imgProps = pdf.getImageProperties(imgData);
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
      pdf.addImage(imgData, "PNG", 0, 40, pdfWidth, pdfHeight);
      pdf.save(`chart_${deviceId}.pdf`);
    });
  };

  const columns = useMemo(() => {
    const baseColumns = [
      {
        name: "Time",
        selector: (row) =>
          new Date(
            row.timestamp || row.dayStart || row.hourStart
          ).toLocaleString("th-TH"),
        sortable: true,
        wrap: true,
      },
    ];
    if (dataType === "hourly" || dataType === "daily") {
      return [
        ...baseColumns,
        {
          name: "PM2.5(avg)",
          selector: (row) =>
            row.data?.avgPm25?.toFixed(1) || row.data?.pm25?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "PM2.5 (min)",
          selector: (row) =>
            row.data?.minPm25?.toFixed(1) || row.data?.pm25?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "PM2.5 (max)",
          selector: (row) =>
            row.data?.maxPm25?.toFixed(1) || row.data?.pm25?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "PM10 (avg)",
          selector: (row) =>
            row.data?.avgPm10?.toFixed(1) || row.data?.pm10?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "PM10 (min)",
          selector: (row) =>
            row.data?.minPm10?.toFixed(1) || row.data?.pm10?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "PM10 (max)",
          selector: (row) =>
            row.data?.maxPm10?.toFixed(1) || row.data?.pm10?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "TSP (avg)",
          selector: (row) =>
            row.data?.avgTsp?.toFixed(1) || row.data?.tsp?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "TSP (min)",
          selector: (row) =>
            row.data?.minTsp?.toFixed(1) || row.data?.tsp?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "TSP (max)",
          selector: (row) =>
            row.data?.maxTsp?.toFixed(1) || row.data?.tsp?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "Noise (avg)",
          selector: (row) =>
            row.data?.avgNoise?.toFixed(1) ||
            row.data?.noise?.toFixed(1) ||
            "-",
          sortable: true,
        },
        {
          name: "Noise (min)",
          selector: (row) =>
            row.data?.minNoise?.toFixed(1) ||
            row.data?.noise?.toFixed(1) ||
            "-",
          sortable: true,
        },
        {
          name: "Noise (max)",
          selector: (row) =>
            row.data?.maxNoise?.toFixed(1) ||
            row.data?.noise?.toFixed(1) ||
            "-",
          sortable: true,
        },
      ];
    } else if (dataType === "raw") {
      return [
        ...baseColumns,
        {
          name: "PM2.5",
          selector: (row) =>
            row.data?.avgPm25?.toFixed(1) || row.data?.pm25?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "PM10",
          selector: (row) =>
            row.data?.avgPm10?.toFixed(1) || row.data?.pm10?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "TSP",
          selector: (row) =>
            row.data?.avgTsp?.toFixed(1) || row.data?.tsp?.toFixed(1) || "-",
          sortable: true,
        },
        {
          name: "Noise",
          selector: (row) =>
            row.data?.avgNoise?.toFixed(1) ||
            row.data?.noise?.toFixed(1) ||
            "-",
          sortable: true,
        },
      ];
    }
  }, [dataType]);

  useEffect(() => {
    handleFetchData();
  }, []);

  return (
    <Box maxW="container.2xl" mx="auto" p={5}>
      <VStack align="stretch" spacing={6}>
        <Flex align="center">
          <VStack spacing={2} align="flex-start" mr={4}>
          <Button
            onClick={() => navigate(-1)}
            leftIcon={<ChevronLeftIcon />}
            variant="ghost"
            width="80px"
          >
            Back
          </Button>
          <Heading>History Report: {deviceId}</Heading>
          </VStack>
        </Flex>

        {/* --- Control Panel --- */}
        <VStack
          p={4}
          bg="gray.50"
          borderRadius="lg"
          spacing={4}
          align="stretch"
          shadow="sm"
        >
          <Wrap spacingX={6} spacingY={4} align="flex-end">
            <FormControl>
              <FormLabel>Data Type</FormLabel>
              <RadioGroup onChange={setDataType} value={dataType}>
                <Stack direction="row">
                  <Radio value="hourly">Hourly</Radio>
                  <Radio value="daily">Daily</Radio>
                  <Radio value="raw">Raw Data</Radio>
                </Stack>
              </RadioGroup>
            </FormControl>
            <FormControl w="200px">
              <FormLabel>Time Range</FormLabel>
              <Select
                size="sm"
                value={rangeType}
                onChange={(e) => setRangeType(e.target.value)}
              >
                <option value="last7days">Last 7 Days</option>
                <option value="last14days">Last 14 Days</option>
                <option value="last30days">Last 1 Month</option>
                <option value="month">Select Month</option>
                <option value="custom">Custom Range</option>
              </Select>
            </FormControl>
            {rangeType === "month" && (
              <FormControl w="200px">
                <FormLabel>Month</FormLabel>
                <Input
                  type="month"
                  size="sm"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
              </FormControl>
            )}
            {rangeType === "custom" && (
              <HStack>
                <FormControl w="200px">
                  <FormLabel>Start</FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={customStart}
                    onChange={(e) => setCustomStart(e.target.value)}
                  />
                </FormControl>
                <FormControl w="200px">
                  <FormLabel>End</FormLabel>
                  <Input
                    type="date"
                    size="sm"
                    value={customEnd}
                    onChange={(e) => setCustomEnd(e.target.value)}
                  />
                </FormControl>
              </HStack>
            )}
          </Wrap>
          <Wrap spacingX={6} spacingY={4} align="flex-end">
            <FormControl w="200px">
              <FormLabel>Metric</FormLabel>
              <Select
                size="sm"
                value={selectedMetric}
                onChange={(e) => setSelectedMetric(e.target.value)}
              >
                {availableMetrics.map((m) => (
                  <option key={m.key} value={m.key}>
                    {m.name}
                  </option>
                ))}
              </Select>
            </FormControl>

            {/* --- REQ 1: เพิ่ม Checkbox และแสดงผลตามเงื่อนไข --- */}
            {(dataType === "hourly" || dataType === "daily") && (
              <FormControl w="auto">
                <FormLabel>Series to Display</FormLabel>
                <HStack>
                  <Checkbox
                    isChecked={showAvg}
                    onChange={(e) => setShowAvg(e.target.checked)}
                  >
                    Average
                  </Checkbox>
                  <Checkbox
                    isChecked={showMax}
                    onChange={(e) => setShowMax(e.target.checked)}
                  >
                    Max
                  </Checkbox>
                  <Checkbox
                    isChecked={showMin}
                    onChange={(e) => setShowMin(e.target.checked)}
                  >
                    Min
                  </Checkbox>
                </HStack>
              </FormControl>
            )}

            <FormControl w="auto">
              <FormLabel>Chart Type</FormLabel>
              <Select
                size="sm"
                value={chartType}
                onChange={(e) => setChartType(e.target.value)}
              >
                <option value="line">Line</option>
                <option value="area">Area</option>
                <option value="bar">Bar</option>
              </Select>
            </FormControl>
            <Button
              colorScheme="blue"
              onClick={handleFetchData}
              isLoading={isLoading}
            >
              Fetch Data
            </Button>
          </Wrap>
        </VStack>

        {/* --- Chart & Table Display --- */}
        {isLoading ? (
          <Center h="350px">
            <Spinner />
          </Center>
        ) : (
          tableData.length > 0 && (
            <VStack align="stretch" spacing={6}>
              <Box
                id="chart-area"
                bg="white"
                p={4}
                borderRadius="lg"
                shadow="md"
              >
                <HistoryChart
                  data={tableData}
                  metric={selectedMetric}
                  type={chartType}
                  dataType={dataType}
                  // --- REQ 1: ส่ง props เพิ่มไปที่ Chart ---
                  showAvg={showAvg}
                  showMin={showMin}
                  showMax={showMax}
                />
              </Box>
              <HStack>
                <Button size="sm" onClick={() => setShowTable(!showTable)}>
                  {showTable ? "Hide Table" : "Show Table"}
                </Button>
                <Button size="sm" colorScheme="green" onClick={exportToExcel}>
                  Export Excel
                </Button>
                <Button
                  size="sm"
                  colorScheme="purple"
                  onClick={exportChartToPDF}
                >
                  Export PDF
                </Button>
              </HStack>
              {showTable && (
                <Box>
                  <DataTable
                    columns={columns}
                    data={tableData}
                    pagination
                    highlightOnHover
                    striped
                    responsive
                  />
                </Box>
              )}
            </VStack>
          )
        )}
      </VStack>
    </Box>
  );
}
